import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import PayoutAccountCard from "@/components/PayoutAccountCard";
import { api, fmtNGN, fmtBps, formatErr } from "@/lib/api";
import { Copy, CheckCircle, Lightning, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AgentDashboard() {
  const [data, setData] = useState({ agent: null, leads: [], commissions: [] });
  const [cfg, setCfg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    const [d, c] = await Promise.all([
      api.get("/agent/dashboard").then((r) => r.data),
      api.get("/public/fee-config").then((r) => r.data).catch(() => null),
    ]);
    setData(d);
    setCfg(c);
  };
  useEffect(() => { reload(); }, []);

  const referralLink = data.agent?.referral_code
    ? `${window.location.origin}/register?ref=${data.agent.referral_code}`
    : "";

  const copy = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    setCopied(true); toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const pendingTotal = (data.agent?.pending_commission || 0) + (data.agent?.pending_salary || 0);
  const threshold = cfg?.early_payout_threshold_ngn ?? 50000;
  const feeBps = cfg?.early_payout_fee_bps ?? 150;
  const feePreview = Math.round(pendingTotal * feeBps) / 10000;
  const netPreview = pendingTotal - feePreview;
  const eligible = pendingTotal >= threshold;
  const accountVerified = !!data.agent?.payout_account?.verified_at;

  const requestEarlyPayout = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.post("/agent/payout-early");
      toast.success(`Same-day payout sent: ${fmtNGN(r.net)} net (fee ${fmtNGN(r.fee)})`);
      setConfirm(false);
      reload();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardShell title="DSA Console" subtitle="Earn commission on every transaction your network generates.">
      <div className="grid lg:grid-cols-4 gap-6 mb-8">
        <StatCard label="Lifetime commission" value={fmtNGN(data.agent?.lifetime_commission || 0)} accent="accent" />
        <StatCard label="Monthly salary" value={fmtNGN(data.agent?.monthly_salary_ngn || 0)} sub={data.agent?.monthly_salary_ngn ? "Set by admin · accrues 1st of month" : "Not yet assigned"} accent="primary" />
        <StatCard label="Pending payout" value={fmtNGN(pendingTotal)} sub={`Commission ${fmtNGN(data.agent?.pending_commission || 0)} · Salary ${fmtNGN(data.agent?.pending_salary || 0)}`} accent="warning" />
        <StatCard label="Network onboarded" value={(data.agent?.schools_onboarded || 0) + (data.agent?.parents_onboarded || 0)} sub={`${data.agent?.schools_onboarded || 0} schools · ${data.agent?.parents_onboarded || 0} parents`} accent="primary" />
      </div>

      {/* Early payout CTA */}
      {eligible && accountVerified && (
        <div className="card mb-8 bg-gradient-to-br from-[#F2B632] to-[#F5C455] text-white border-0 grain" data-testid="early-payout-cta">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/15 grid place-items-center shrink-0">
                <Lightning size={24} weight="fill" className="text-white" />
              </div>
              <div>
                <div className="overline !text-white/85">Same-day payout available</div>
                <h3 className="font-display text-2xl mt-1 font-medium">Don't wait for month-end. Get paid today.</h3>
                <p className="text-sm text-white/85 mt-1.5 max-w-2xl">
                  You have <span className="font-semibold">{fmtNGN(pendingTotal)}</span> pending. We'll wire <span className="font-semibold">{fmtNGN(netPreview)}</span> to your verified NUBAN today for a <span className="font-semibold">{fmtBps(feeBps)}</span> early-access fee ({fmtNGN(feePreview)}).
                </p>
              </div>
            </div>
            <button onClick={() => setConfirm(true)} className="btn-secondary !bg-white !text-[#F2B632] hover:!bg-[#F9F7F4] shrink-0" data-testid="early-payout-button">
              <Lightning size={16} weight="fill" /> Request early payout
            </button>
          </div>
        </div>
      )}
      {pendingTotal > 0 && pendingTotal < threshold && (
        <div className="mb-8 px-5 py-3 rounded-2xl bg-[#FBF1DC] text-[#7a5a14] text-sm flex items-center gap-2" data-testid="early-payout-threshold-hint">
          <Lightning size={14} weight="fill" className="text-[#B45309]" />
          Early payout unlocks at {fmtNGN(threshold)}. You're {fmtNGN(threshold - pendingTotal)} away.
        </div>
      )}
      {eligible && !accountVerified && (
        <div className="mb-8 px-5 py-3 rounded-2xl bg-[#FBE3E3] text-[#7a2424] text-sm flex items-center gap-2" data-testid="early-payout-needs-account">
          <Warning size={14} weight="fill" className="text-[#C93B3B]" />
          You qualify for early payout — add a verified NUBAN below to unlock.
        </div>
      )}

      {/* Payout account card */}
      <div className="mb-8">
        <PayoutAccountCard />
      </div>

      <div className="card mb-8 bg-gradient-to-br from-[#132A13] to-[#0A3622] text-white border-0">
        <div className="overline !text-[#F2B632]">Your referral link</div>
        <h2 className="font-display text-2xl mt-2 font-medium">Code: <span className="font-mono text-[#F2B632]">{data.agent?.referral_code}</span></h2>
        <p className="text-sm text-white/70 mt-2">Share this link with schools and parents. You earn 25% of every platform fee they generate.</p>
        <div className="mt-5 flex gap-3">
          <input className="input bg-white/10 border-white/10 text-white" readOnly value={referralLink} data-testid="referral-link-input" />
          <button onClick={copy} className="btn-primary !bg-[#F2B632]" data-testid="copy-referral-button">
            {copied ? <><CheckCircle size={16} weight="fill" /> Copied</> : <><Copy size={16} weight="bold" /> Copy</>}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-display text-lg font-medium mb-4">Your network</h3>
          {data.leads.length === 0 ? (
            <p className="text-sm text-[#4F5D4F] py-6 text-center">No referrals yet — share your link.</p>
          ) : (
            <table className="dense">
              <thead><tr><th>Name</th><th>Role</th><th>Joined</th></tr></thead>
              <tbody>
                {data.leads.slice(0, 12).map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <td>{l.referred_user?.name || "—"}</td>
                    <td><span className="badge badge-info">{l.referred_role}</span></td>
                    <td>{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <h3 className="font-display text-lg font-medium mb-4">Commission ledger</h3>
          {data.commissions.length === 0 ? (
            <p className="text-sm text-[#4F5D4F] py-6 text-center">No commissions yet.</p>
          ) : (
            <table className="dense">
              <thead><tr><th>Date</th><th>Platform fee</th><th>Your cut</th><th>Status</th></tr></thead>
              <tbody>
                {data.commissions.slice(0, 12).map((c) => (
                  <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                    <td>{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="font-mono">{fmtNGN(c.platform_fee)}</td>
                    <td className="font-mono text-[#2D7A5D] font-medium">{fmtNGN(c.commission)}</td>
                    <td><span className={`badge ${c.status === "paid" ? "badge-success" : "badge-warning"}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={() => !loading && setConfirm(false)} data-testid="early-payout-modal">
          <div className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl border border-[#E5E3DB]" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-xl bg-[#F2B632]/15 grid place-items-center mb-4">
              <Lightning size={24} weight="fill" className="text-[#F2B632]" />
            </div>
            <h3 className="font-display text-2xl font-medium text-[#132A13]">Request same-day payout?</h3>
            <p className="text-sm text-[#4F5D4F] mt-2">We'll wire to your verified NUBAN today. The early-access fee is netted off automatically.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <Cell k="Pending gross" v={fmtNGN(pendingTotal)} />
              <Cell k={`Early-access fee (${fmtBps(feeBps)})`} v={`-${fmtNGN(feePreview)}`} tone="warning" />
              <Cell k="Net to your NUBAN" v={fmtNGN(netPreview)} tone="success" testid="early-payout-net" />
              <Cell k="Account" v={`****${data.agent?.payout_account?.account_number?.slice(-4)}`} />
            </div>
            <p className="text-xs text-[#4F5D4F] mt-4">By confirming, your pending commission and salary balances reset to zero. Future earnings continue to accrue normally.</p>
            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => setConfirm(false)} disabled={loading} className="btn-ghost text-sm" data-testid="early-payout-cancel">Cancel</button>
              <button onClick={requestEarlyPayout} disabled={loading} className="btn-primary text-sm" data-testid="early-payout-confirm">
                {loading ? "Processing…" : <><Lightning size={14} weight="fill" /> Pay me {fmtNGN(netPreview)} now</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function Cell({ k, v, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-[#F9F7F4] text-[#132A13]",
    success: "bg-[#E2F3EB] text-[#2D7A5D]",
    warning: "bg-[#FBF1DC] text-[#B45309]",
  };
  return (
    <div className={`rounded-xl p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] opacity-80 font-semibold leading-tight">{k}</div>
      <div className="font-display text-lg font-semibold mt-1.5">{v}</div>
    </div>
  );
}
